// AGC: TierConfigAdmin - Admin panel for subscription tier configuration
// AGC: EDITABLE version - allows admin to modify tier limits and features
// AGC: Fetches from and saves to Firestore via API

import React, { useState, useEffect } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { getBackendUrl } from '../../utils/backendUrl'
import { 
  Crown, 
  Star, 
  Gift,
  Check,
  X,
  Mic,
  Map,
  ListTodo,
  Users,
  Calculator,
  MessageSquare,
  Cloud,
  Smartphone,
  BarChart3,
  WifiOff,
  Zap,
  Save,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Settings,
  RotateCcw,
} from 'lucide-react'

/**
 * AGC: Tier tab type
 */
type TierTab = 'free' | 'pro' | 'premium'

/**
 * AGC: Tier configuration interface
 */
interface TierConfig {
  name: string
  displayName: string
  description?: string
  price: {
    monthly: number
    annual: number
    currency: string
  }
  limits: {
    maxHectares: number | null
    maxFields: number | null
    maxTasksPerMonth: number | null
    maxVoiceCommandsPerMonth: number | null
    maxWhatsAppAudioPerMonth: number | null
    maxTeamMembers: number | null
    hectareCalculatorUsesPerMonth: number | null
  }
  features: {
    voiceMode: 'A' | 'B'
    hasOfflineVoice: boolean
    hasWhatsAppIntegration: boolean
    hasAdvancedWeather: boolean
    hasYieldPotential: boolean
    hasDroidGrid: boolean
    hasHumanAdvisor: boolean
    hasMultiFarm: boolean
    hasAdvancedAnalytics: boolean
    hasApiAccess: boolean
    hasPrioritySupport: boolean
    hasExportCapabilities: boolean
    hasSatelliteImagery: boolean
    hasPrescriptionMaps: boolean
  }
  voiceSettings: {
    enableGPTFallback: boolean
    enableOfflineQueue: boolean
    autoProcessQueue: boolean
    maxQueueSize: number
    defaultLanguage: string
  }
  color: string
  icon: string
  badge: string
  order: number
}

/**
 * AGC: Default tier configurations
 */
const DEFAULT_TIER_CONFIG: Record<TierTab, TierConfig> = {
  free: {
    name: 'FREE',
    displayName: 'Produtor Iniciante',
    description: 'Ideal para pequenos produtores que estão começando',
    price: { monthly: 0, annual: 0, currency: 'BRL' },
    limits: {
      maxHectares: 50,
      maxFields: 3,
      maxTasksPerMonth: 30,
      maxVoiceCommandsPerMonth: 50,
      maxWhatsAppAudioPerMonth: 0,
      maxTeamMembers: 0,
      hectareCalculatorUsesPerMonth: 3,
    },
    features: {
      voiceMode: 'B',
      hasOfflineVoice: false,
      hasWhatsAppIntegration: false,
      hasAdvancedWeather: false,
      hasYieldPotential: false,
      hasDroidGrid: false,
      hasHumanAdvisor: false,
      hasMultiFarm: false,
      hasAdvancedAnalytics: false,
      hasApiAccess: false,
      hasPrioritySupport: false,
      hasExportCapabilities: false,
      hasSatelliteImagery: false,
      hasPrescriptionMaps: false,
    },
    voiceSettings: {
      enableGPTFallback: true,
      enableOfflineQueue: true,
      autoProcessQueue: true,
      maxQueueSize: 50,
      defaultLanguage: 'pt-BR',
    },
    color: 'emerald',
    icon: 'Gift',
    badge: 'GRÁTIS',
    order: 0,
  },
  pro: {
    name: 'PRO',
    displayName: 'Produtor Profissional',
    description: 'Para produtores em crescimento com mais recursos',
    price: { monthly: 99, annual: 990, currency: 'BRL' },
    limits: {
      maxHectares: 500,
      maxFields: 20,
      maxTasksPerMonth: 300,
      maxVoiceCommandsPerMonth: 500,
      maxWhatsAppAudioPerMonth: 100,
      maxTeamMembers: 5,
      hectareCalculatorUsesPerMonth: null,
    },
    features: {
      voiceMode: 'A',
      hasOfflineVoice: true,
      hasWhatsAppIntegration: true,
      hasAdvancedWeather: true,
      hasYieldPotential: true,
      hasDroidGrid: true,
      hasHumanAdvisor: false,
      hasMultiFarm: false,
      hasAdvancedAnalytics: false,
      hasApiAccess: false,
      hasPrioritySupport: false,
      hasExportCapabilities: true,
      hasSatelliteImagery: true,
      hasPrescriptionMaps: true,
    },
    voiceSettings: {
      enableGPTFallback: true,
      enableOfflineQueue: true,
      autoProcessQueue: true,
      maxQueueSize: 100,
      defaultLanguage: 'pt-BR',
    },
    color: 'blue',
    icon: 'Star',
    badge: 'PRO',
    order: 1,
  },
  premium: {
    name: 'PREMIUM',
    displayName: 'Produtor Enterprise',
    description: 'Solução completa para grandes operações e multi-fazendas',
    price: { monthly: 299, annual: 2990, currency: 'BRL' },
    limits: {
      maxHectares: null,
      maxFields: null,
      maxTasksPerMonth: null,
      maxVoiceCommandsPerMonth: null,
      maxWhatsAppAudioPerMonth: null,
      maxTeamMembers: null,
      hectareCalculatorUsesPerMonth: null,
    },
    features: {
      voiceMode: 'A',
      hasOfflineVoice: true,
      hasWhatsAppIntegration: true,
      hasAdvancedWeather: true,
      hasYieldPotential: true,
      hasDroidGrid: true,
      hasHumanAdvisor: true,
      hasMultiFarm: true,
      hasAdvancedAnalytics: true,
      hasApiAccess: true,
      hasPrioritySupport: true,
      hasExportCapabilities: true,
      hasSatelliteImagery: true,
      hasPrescriptionMaps: true,
    },
    voiceSettings: {
      enableGPTFallback: true,
      enableOfflineQueue: true,
      autoProcessQueue: true,
      maxQueueSize: 200,
      defaultLanguage: 'pt-BR',
    },
    color: 'yellow',
    icon: 'Crown',
    badge: 'PREMIUM',
    order: 2,
  },
}

/**
 * AGC: TierConfigAdmin Component - Editable version
 */
const BACKEND_URL = getBackendUrl()

export const TierConfigAdmin: React.FC = () => {
  const { t } = useTranslation()
  const { getAuthToken } = useAuth()
  const [activeTab, setActiveTab] = useState<TierTab>('free')
  const [tierConfigs, setTierConfigs] = useState<Record<TierTab, TierConfig>>(DEFAULT_TIER_CONFIG)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  // AGC: Fetch tier configs on mount
  useEffect(() => {
    fetchTierConfigs()
  }, [])

  // AGC: Fetch tier configurations from API
  const fetchTierConfigs = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/tier-config`)
      const data = await response.json()
      
      if (data.success && data.tiers) {
        // AGC: Merge with defaults to ensure all fields exist
        const mergedConfigs = {
          free: { ...DEFAULT_TIER_CONFIG.free, ...data.tiers.free },
          pro: { ...DEFAULT_TIER_CONFIG.pro, ...data.tiers.pro },
          premium: { ...DEFAULT_TIER_CONFIG.premium, ...data.tiers.premium },
        }
        setTierConfigs(mergedConfigs)
      }
    } catch (err) {
      console.error('❌ AGC: Error fetching tier configs:', err)
      setError(t('admin.loadError'))
    } finally {
      setIsLoading(false)
    }
  }

  // AGC: Save tier configuration
  const saveTierConfig = async (tier: TierTab) => {
    setIsSaving(true)
    setError(null)
    setSuccessMessage(null)
    
    try {
      const token = await getAuthToken()
      const response = await fetch(`${BACKEND_URL}/api/v1/tier-config/${tier}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(tierConfigs[tier]),
      })
      
      const data = await response.json()
      
      if (data.success) {
        setSuccessMessage(t('admin.saveSuccess').replace('{tier}', tier.toUpperCase()))
        setHasChanges(false)
        setTimeout(() => setSuccessMessage(null), 3000)
      } else {
        throw new Error(data.error || t('admin.saveError'))
      }
    } catch (err) {
      console.error('❌ AGC: Error saving tier config:', err)
      setError(t('admin.saveError'))
    } finally {
      setIsSaving(false)
    }
  }

  // AGC: Reset tier to defaults
  const resetTierToDefaults = async (tier: TierTab) => {
    if (!confirm(t('admin.resetConfirm').replace('{tier}', tier.toUpperCase()))) {
      return
    }
    
    setIsSaving(true)
    setError(null)
    
    try {
      const token = await getAuthToken()
      const response = await fetch(`${BACKEND_URL}/api/v1/tier-config/reset/${tier}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      
      const data = await response.json()
      
      if (data.success) {
        setTierConfigs(prev => ({
          ...prev,
          [tier]: DEFAULT_TIER_CONFIG[tier],
        }))
        setSuccessMessage(t('admin.resetSuccess').replace('{tier}', tier.toUpperCase()))
        setHasChanges(false)
        setTimeout(() => setSuccessMessage(null), 3000)
      }
    } catch (err) {
      console.error('❌ AGC: Error resetting tier:', err)
      setError(t('admin.resetError'))
    } finally {
      setIsSaving(false)
    }
  }

  // AGC: Update a limit value
  const updateLimit = (tier: TierTab, key: keyof TierConfig['limits'], value: number | null) => {
    setTierConfigs(prev => ({
      ...prev,
      [tier]: {
        ...prev[tier],
        limits: {
          ...prev[tier].limits,
          [key]: value,
        },
      },
    }))
    setHasChanges(true)
  }

  // AGC: Update a feature toggle
  const updateFeature = (tier: TierTab, key: keyof TierConfig['features'], value: boolean | string) => {
    setTierConfigs(prev => ({
      ...prev,
      [tier]: {
        ...prev[tier],
        features: {
          ...prev[tier].features,
          [key]: value,
        },
      },
    }))
    setHasChanges(true)
  }

  // AGC: Update voice settings
  const updateVoiceSetting = (tier: TierTab, key: keyof TierConfig['voiceSettings'], value: boolean | number | string) => {
    setTierConfigs(prev => ({
      ...prev,
      [tier]: {
        ...prev[tier],
        voiceSettings: {
          ...prev[tier].voiceSettings,
          [key]: value,
        },
      },
    }))
    setHasChanges(true)
  }

  // AGC: Update price
  const updatePrice = (tier: TierTab, key: 'monthly' | 'annual', value: number) => {
    setTierConfigs(prev => ({
      ...prev,
      [tier]: {
        ...prev[tier],
        price: {
          ...prev[tier].price,
          [key]: value,
        },
      },
    }))
    setHasChanges(true)
  }

  const activeTier = tierConfigs[activeTab]

  // AGC: Color classes based on tier
  const getColorClasses = (tier: TierTab) => {
    switch (tier) {
      case 'free':
        return {
          bg: 'bg-emerald-50 dark:bg-emerald-900/20',
          border: 'border-emerald-200 dark:border-emerald-800',
          text: 'text-emerald-700 dark:text-emerald-400',
          badge: 'bg-emerald-500',
          button: 'bg-emerald-500 hover:bg-emerald-600',
          tab: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-b-2 border-emerald-500',
        }
      case 'pro':
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-blue-200 dark:border-blue-800',
          text: 'text-blue-700 dark:text-blue-400',
          badge: 'bg-blue-500',
          button: 'bg-blue-500 hover:bg-blue-600',
          tab: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-b-2 border-blue-500',
        }
      case 'premium':
        return {
          bg: 'bg-yellow-50 dark:bg-yellow-900/20',
          border: 'border-yellow-200 dark:border-yellow-800',
          text: 'text-yellow-700 dark:text-yellow-400',
          badge: 'bg-yellow-500',
          button: 'bg-yellow-500 hover:bg-yellow-600',
          tab: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border-b-2 border-yellow-500',
        }
    }
  }

  const colors = getColorClasses(activeTab)
  const TierIcon = activeTab === 'free' ? Gift : activeTab === 'pro' ? Star : Crown

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-2 text-slate-600 dark:text-slate-300">Carregando configurações...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* AGC: Header */}
      <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Settings className="w-8 h-8 text-blue-500" />
            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {t('admin.tierConfigTitle')}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Edite limites, recursos e configurações de voz para cada plano
              </p>
            </div>
          </div>
          <button
            onClick={fetchTierConfigs}
            className="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/[0.02] rounded-lg"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* AGC: Error/Success Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <span className="text-red-700 dark:text-red-400">{error}</span>
        </div>
      )}
      
      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center space-x-2">
          <Check className="w-5 h-5 text-green-500" />
          <span className="text-green-700 dark:text-green-400">{successMessage}</span>
        </div>
      )}

      {/* AGC: Tabs */}
      <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none overflow-hidden">
        <div className="flex border-b border-slate-200 dark:border-white/5">
          {(['free', 'pro', 'premium'] as TierTab[]).map((tier) => (
            <button
              key={tier}
              onClick={() => setActiveTab(tier)}
              className={`flex-1 flex items-center justify-center space-x-2 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === tier
                  ? getColorClasses(tier).tab
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-white/[0.02]'
              }`}
            >
              {tier === 'free' && <Gift className="w-5 h-5" />}
              {tier === 'pro' && <Star className="w-5 h-5" />}
              {tier === 'premium' && <Crown className="w-5 h-5" />}
              <span>{tier.toUpperCase()}</span>
              <span className="text-xs opacity-75">
                R$ {tierConfigs[tier].price.monthly}/mês
              </span>
            </button>
          ))}
        </div>

        {/* AGC: Tab Content */}
        <div className="p-6">
          {/* AGC: Tier Header */}
          <div className={`p-4 rounded-lg ${colors.bg} border ${colors.border} mb-6`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className={`p-3 ${colors.bg} rounded-full`}>
                  <TierIcon className={`w-8 h-8 ${colors.text}`} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    {activeTier.displayName}
                    <span className={`px-2 py-0.5 text-xs ${colors.badge} text-white rounded-full`}>
                      {activeTier.name}
                    </span>
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                    {activeTier.description}
                  </p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => resetTierToDefaults(activeTab)}
                  className="px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg flex items-center space-x-1"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Resetar</span>
                </button>
                <button
                  onClick={() => saveTierConfig(activeTab)}
                  disabled={isSaving || !hasChanges}
                  className={`px-4 py-2 text-sm text-white rounded-lg flex items-center space-x-2 ${colors.button} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  <span>{t('admin.save')}</span>
                </button>
              </div>
            </div>
          </div>

          {/* AGC: Pricing Section */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
              💰 Preços
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <EditableNumberInput
                label="Preço Mensal (R$)"
                value={activeTier.price.monthly}
                onChange={(v) => updatePrice(activeTab, 'monthly', v ?? 0)}
                min={0}
                allowNull={false}
              />
              <EditableNumberInput
                label="Preço Anual (R$)"
                value={activeTier.price.annual}
                onChange={(v) => updatePrice(activeTab, 'annual', v ?? 0)}
                min={0}
                allowNull={false}
              />
            </div>
          </div>

          {/* AGC: Limits Section */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Limites de Uso
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <EditableLimitCard
                icon={<Map className="w-5 h-5 text-green-500" />}
                label="Hectares"
                value={activeTier.limits.maxHectares}
                onChange={(v) => updateLimit(activeTab, 'maxHectares', v)}
              />
              <EditableLimitCard
                icon={<Map className="w-5 h-5 text-blue-500" />}
                label="Talhões"
                value={activeTier.limits.maxFields}
                onChange={(v) => updateLimit(activeTab, 'maxFields', v)}
              />
              <EditableLimitCard
                icon={<ListTodo className="w-5 h-5 text-purple-500" />}
                label="Tarefas/Mês"
                value={activeTier.limits.maxTasksPerMonth}
                onChange={(v) => updateLimit(activeTab, 'maxTasksPerMonth', v)}
              />
              <EditableLimitCard
                icon={<Mic className="w-5 h-5 text-red-500" />}
                label="Comandos de Voz/Mês"
                value={activeTier.limits.maxVoiceCommandsPerMonth}
                onChange={(v) => updateLimit(activeTab, 'maxVoiceCommandsPerMonth', v)}
              />
              <EditableLimitCard
                icon={<MessageSquare className="w-5 h-5 text-green-600" />}
                label="Áudios WhatsApp/Mês"
                value={activeTier.limits.maxWhatsAppAudioPerMonth}
                onChange={(v) => updateLimit(activeTab, 'maxWhatsAppAudioPerMonth', v)}
              />
              <EditableLimitCard
                icon={<Users className="w-5 h-5 text-indigo-500" />}
                label="Membros da Equipe"
                value={activeTier.limits.maxTeamMembers}
                onChange={(v) => updateLimit(activeTab, 'maxTeamMembers', v)}
              />
              <EditableLimitCard
                icon={<Calculator className="w-5 h-5 text-orange-500" />}
                label="Calculadora Hectares/Mês"
                value={activeTier.limits.hectareCalculatorUsesPerMonth}
                onChange={(v) => updateLimit(activeTab, 'hectareCalculatorUsesPerMonth', v)}
              />
            </div>
          </div>

          {/* AGC: Voice Settings */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
              <Mic className="w-5 h-5" />
              Configurações de Voz
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* AGC: Voice Mode Selector */}
              <div className="bg-slate-50 dark:bg-white/[0.02] rounded-lg p-4">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Modo de Transcrição
                </label>
                <div className="flex space-x-2">
                  <button
                    onClick={() => updateFeature(activeTab, 'voiceMode', 'B')}
                    className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                      activeTier.features.voiceMode === 'B'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-slate-200 dark:border-white/5 hover:border-slate-200'
                    }`}
                  >
                    <Smartphone className={`w-5 h-5 mx-auto mb-1 ${activeTier.features.voiceMode === 'B' ? 'text-blue-600' : 'text-slate-400 dark:text-slate-500'}`} />
                    <p className="text-xs font-medium">Nativo (B)</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Grátis</p>
                  </button>
                  <button
                    onClick={() => updateFeature(activeTab, 'voiceMode', 'A')}
                    className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                      activeTier.features.voiceMode === 'A'
                        ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                        : 'border-slate-200 dark:border-white/5 hover:border-slate-200'
                    }`}
                  >
                    <Cloud className={`w-5 h-5 mx-auto mb-1 ${activeTier.features.voiceMode === 'A' ? 'text-yellow-600' : 'text-slate-400 dark:text-slate-500'}`} />
                    <p className="text-xs font-medium">Whisper (A)</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Premium</p>
                  </button>
                </div>
              </div>

              {/* AGC: Voice feature toggles */}
              <div className="bg-slate-50 dark:bg-white/[0.02] rounded-lg p-4 space-y-3">
                <FeatureToggle
                  label="Voz Offline"
                  description="Funciona sem internet"
                  icon={<WifiOff className="w-4 h-4" />}
                  enabled={activeTier.features.hasOfflineVoice}
                  onChange={(v) => updateFeature(activeTab, 'hasOfflineVoice', v)}
                />
                <FeatureToggle
                  label="GPT Fallback"
                  description="Usa GPT quando reconhecimento falha"
                  icon={<Zap className="w-4 h-4" />}
                  enabled={activeTier.voiceSettings.enableGPTFallback}
                  onChange={(v) => updateVoiceSetting(activeTab, 'enableGPTFallback', v)}
                />
                <FeatureToggle
                  label="Fila Offline"
                  description="Salva comandos quando offline"
                  icon={<Cloud className="w-4 h-4" />}
                  enabled={activeTier.voiceSettings.enableOfflineQueue}
                  onChange={(v) => updateVoiceSetting(activeTab, 'enableOfflineQueue', v)}
                />
              </div>
            </div>
          </div>

          {/* AGC: Features Section */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Recursos e Integrações
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <FeatureToggle
                label="Integração WhatsApp"
                enabled={activeTier.features.hasWhatsAppIntegration}
                onChange={(v) => updateFeature(activeTab, 'hasWhatsAppIntegration', v)}
              />
              <FeatureToggle
                label="Clima Avançado (7 dias)"
                enabled={activeTier.features.hasAdvancedWeather}
                onChange={(v) => updateFeature(activeTab, 'hasAdvancedWeather', v)}
              />
              <FeatureToggle
                label="Potencial de Rendimento"
                enabled={activeTier.features.hasYieldPotential}
                onChange={(v) => updateFeature(activeTab, 'hasYieldPotential', v)}
              />
              <FeatureToggle
                label="Droid Grid (Gestão)"
                enabled={activeTier.features.hasDroidGrid}
                onChange={(v) => updateFeature(activeTab, 'hasDroidGrid', v)}
              />
              <FeatureToggle
                label="Consultor Humano"
                enabled={activeTier.features.hasHumanAdvisor}
                onChange={(v) => updateFeature(activeTab, 'hasHumanAdvisor', v)}
              />
              <FeatureToggle
                label="Multi-Fazenda"
                enabled={activeTier.features.hasMultiFarm}
                onChange={(v) => updateFeature(activeTab, 'hasMultiFarm', v)}
              />
              <FeatureToggle
                label="Analytics Avançado"
                enabled={activeTier.features.hasAdvancedAnalytics}
                onChange={(v) => updateFeature(activeTab, 'hasAdvancedAnalytics', v)}
              />
              <FeatureToggle
                label="API Access"
                enabled={activeTier.features.hasApiAccess}
                onChange={(v) => updateFeature(activeTab, 'hasApiAccess', v)}
              />
              <FeatureToggle
                label="Suporte Prioritário"
                enabled={activeTier.features.hasPrioritySupport}
                onChange={(v) => updateFeature(activeTab, 'hasPrioritySupport', v)}
              />
              <FeatureToggle
                label="Exportação de Dados"
                enabled={activeTier.features.hasExportCapabilities}
                onChange={(v) => updateFeature(activeTab, 'hasExportCapabilities', v)}
              />
              <FeatureToggle
                label="Imagens de Satélite"
                enabled={activeTier.features.hasSatelliteImagery}
                onChange={(v) => updateFeature(activeTab, 'hasSatelliteImagery', v)}
              />
              <FeatureToggle
                label="Mapas de Prescrição"
                enabled={activeTier.features.hasPrescriptionMaps}
                onChange={(v) => updateFeature(activeTab, 'hasPrescriptionMaps', v)}
              />
            </div>
          </div>

          {/* AGC: Save Button at Bottom */}
          {hasChanges && (
            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200 dark:border-white/5">
              <button
                onClick={() => {
                  setTierConfigs(DEFAULT_TIER_CONFIG)
                  setHasChanges(false)
                }}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={() => saveTierConfig(activeTab)}
                disabled={isSaving}
                className={`px-6 py-2 text-white rounded-lg flex items-center space-x-2 ${colors.button} disabled:opacity-50`}
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span>Salvar Alterações</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * AGC: Editable Limit Card Component
 */
interface EditableLimitCardProps {
  icon: React.ReactNode
  label: string
  value: number | null
  onChange: (value: number | null) => void
}

const EditableLimitCard: React.FC<EditableLimitCardProps> = ({ icon, label, value, onChange }) => {
  const isUnlimited = value === null
  const [localValue, setLocalValue] = useState<string>(value?.toString() ?? '')

  const handleToggleUnlimited = () => {
    if (isUnlimited) {
      onChange(0)
      setLocalValue('0')
    } else {
      onChange(null)
      setLocalValue('')
    }
  }

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setLocalValue(val)
    const num = parseInt(val, 10)
    if (!isNaN(num) && num >= 0) {
      onChange(num)
    }
  }

  return (
    <div className="bg-slate-50 dark:bg-white/[0.02] rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          {icon}
          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{label}</span>
        </div>
        <button
          onClick={handleToggleUnlimited}
          className={`px-2 py-1 text-xs rounded ${
            isUnlimited
              ? 'bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400'
              : 'bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-slate-300'
          }`}
        >
          {isUnlimited ? '∞ Ilimitado' : 'Limitado'}
        </button>
      </div>
      {!isUnlimited && (
        <input
          type="number"
          value={localValue}
          onChange={handleValueChange}
          min={0}
          className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900 text-lg font-bold"
        />
      )}
      {isUnlimited && (
        <p className="text-2xl font-bold text-green-600 dark:text-green-400">∞</p>
      )}
    </div>
  )
}

/**
 * AGC: Editable Number Input Component
 */
interface EditableNumberInputProps {
  label: string
  value: number | null
  onChange: (value: number | null) => void
  min?: number
  allowNull?: boolean
}

const EditableNumberInput: React.FC<EditableNumberInputProps> = ({ 
  label, value, onChange, min = 0, allowNull = true 
}) => {
  const [localValue, setLocalValue] = useState<string>(value?.toString() ?? '')

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setLocalValue(val)
    if (val === '' && allowNull) {
      onChange(null)
    } else {
      const num = parseFloat(val)
      if (!isNaN(num) && num >= min) {
        onChange(num)
      }
    }
  }

  return (
    <div className="bg-slate-50 dark:bg-white/[0.02] rounded-lg p-4">
      <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">
        {label}
      </label>
      <input
        type="number"
        value={localValue}
        onChange={handleValueChange}
        min={min}
        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900 text-lg font-bold"
      />
    </div>
  )
}

/**
 * AGC: Feature Toggle Component
 */
interface FeatureToggleProps {
  label: string
  description?: string
  icon?: React.ReactNode
  enabled: boolean
  onChange: (enabled: boolean) => void
}

const FeatureToggle: React.FC<FeatureToggleProps> = ({ label, description, icon, enabled, onChange }) => {
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
      enabled ? 'bg-success-50 dark:bg-emerald-500/15' : 'bg-slate-50 dark:bg-white/[0.02]'
    }`}>
      <div className="flex items-center space-x-2">
        {icon && <span className={enabled ? 'text-brand-primary dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}>{icon}</span>}
        <div>
          <span className={`text-sm font-medium ${enabled ? 'text-slate-900 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'}`}>
            {label}
          </span>
          {description && (
            <p className="text-xs text-slate-500 dark:text-slate-400">{description}</p>
          )}
        </div>
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          enabled ? 'bg-brand-primary dark:bg-brand-light' : 'bg-slate-200 dark:bg-white/10'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  )
}

export default TierConfigAdmin
